<template>
    <div class="matches-toolbar">
        <div class="toolbar-left">
            <div class="select-dropdown" v-click-outside="{ handler: closeDropdown, middleware: isOutsidePanel }">
                <button type="button" ref="toggleBtn" class="btn btn-light dropdown-toggle legitRipple" :disabled="isMatching || checkedItems.length === 0" @click="toggleDropdown">
                    <i v-if="isMatching" class="icon-spinner4 spinner mr-1"></i>
                    {{ $t('labels.create_integration_products_match_button') }}
                </button>
                <Teleport to="body">
                    <div
                        v-if="isDropdownOpen"
                        ref="panelEl"
                        class="select-panel"
                        :style="{ top: panelPosition.top + 'px', left: panelPosition.left + 'px' }"
                    >
                        <div class="select-panel-group">
                            <span class="select-panel-label">{{ $t('labels.connect_integration_products_search_match_label') }}</span>
                            <div class="select-panel-options">
                                <label class="select-panel-option">
                                    <Radio :model-value="searchMatch" @update:modelValue="searchMatch = true"/>
                                    {{ $t('buttons.yes') }}
                                </label>
                                <label class="select-panel-option">
                                    <Radio :model-value="!searchMatch" @update:modelValue="searchMatch = false"/>
                                    {{ $t('buttons.no') }}
                                </label>
                            </div>
                        </div>
                        <div class="select-panel-group">
                            <span class="select-panel-label">{{ $t('labels.connect_integration_products_match_mode_label') }}</span>
                            <div class="select-panel-options">
                                <label class="select-panel-option">
                                    <AppCheckbox :model-value="matchModes.includes('exact')" :disabled="!searchMatch" @update:model-value="toggleMatchMode('exact')"/>
                                    {{ $t('labels.create_integration_products_exact_match_mode') }}
                                </label>
                                <label class="select-panel-option" :title="hasNoAiBalance ? $t('alerts.insufficient_ai_balance_for_ai_substitution') : ''">
                                    <AppCheckbox :model-value="matchModes.includes('ai')" :disabled="!searchMatch || hasNoAiBalance" @update:model-value="toggleMatchMode('ai')"/>
                                    {{ $t('labels.create_integration_products_ai_match_mode') }}
                                </label>
                            </div>
                        </div>
                        <div class="select-panel-group">
                            <span class="select-panel-label">{{ $t('labels.connect_integration_products_create_if_not_matched_label') }}</span>
                            <div class="select-panel-options">
                                <label class="select-panel-option">
                                    <Radio :model-value="createIfNotMatched" @update:modelValue="createIfNotMatched = true"/>
                                    {{ $t('buttons.yes') }}
                                </label>
                                <label class="select-panel-option">
                                    <Radio :model-value="!createIfNotMatched" @update:modelValue="createIfNotMatched = false"/>
                                    {{ $t('buttons.no') }}
                                </label>
                            </div>
                        </div>
                        <div class="select-panel-footer">
                            <AppButton color="indigo" :disabled="isMatching" @click="applySelection">
                                <i v-if="isMatching" class="icon-spinner4 spinner mr-1"></i>
                                {{ $t('buttons.apply') }}
                            </AppButton>
                        </div>
                    </div>
                </Teleport>
            </div>
            <div class="search-box">
                <i class="icon-search4 search-icon"></i>
                <input
                    type="search"
                    v-model="search"
                    :placeholder="$t('labels.search')"
                    :aria-label="$t('labels.search')"
                    class="search-input"
                >
            </div>
        </div>
        <div role="tablist" :aria-label="$t('labels.matches_filter')" class="tabs">
            <button
                type="button"
                role="tab"
                :aria-selected="activeTab === 'all'"
                :class="['tab', { active: activeTab === 'all' }]"
                @click="activeTab = 'all'"
            >
                {{ $t('labels.all') }}<span class="tab-count">{{ items.length }}</span>
            </button>
            <button
                type="button"
                role="tab"
                :aria-selected="activeTab === 'chosen'"
                :class="['tab', { active: activeTab === 'chosen' }]"
                @click="activeTab = 'chosen'"
            >
                {{ $t('labels.chosen') }}<span class="tab-count">{{ chosenItems.length }}</span>
            </button>
            <button
                type="button"
                role="tab"
                :aria-selected="activeTab === 'not_chosen'"
                :class="['tab', { active: activeTab === 'not_chosen' }]"
                @click="activeTab = 'not_chosen'"
            >
                {{ $t('labels.not_chosen') }}<span class="tab-count">{{ notChosenItems.length }}</span>
            </button>
        </div>
    </div>
    <div class="table-scroll">
        <table class="table table-bordered table-fixed">
            <thead>
                <tr>
                    <th width="60px"><AppCheckbox :model-value="allSelected" @update:model-value="toggleAll"/></th>
                    <th width="50%">Категорія публікації</th>
                    <th width="50%">Тип товару</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="item in filteredItems" :key="item.uuid">
                    <td><AppCheckbox :model-value="item.selected" @update:model-value="val => item.selected = val"/></td>
                    <td>
                        {{ item.name }}<br>
                        <span class="text-muted">{{ $t('labels.connect_integration_products_category_publications_count', { count: item.productsCount }) }}</span>
                    </td>
                    <td>
                        <Autocomplete
                            :url="`/projects/${$route.params.projectUuid}/pim/types`"
                            :model-value="selectedTypes[item.uuid]?.uuid"
                            :selected-value="selectedTypes[item.uuid]?.name"
                            :can-be-empty="true"
                            :placeholder="$t('labels.do_not_create')"
                            @update:model-value="value => selectType(item.uuid, value)"
                        />
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>
<script>
import AppButton from "../../../../components/new/button/AppButton.vue";
import AppCheckbox from "../../../../components/new/form/checkbox/AppCheckbox.vue";
import Radio from "../../../../components/new/form/radio/Radio.vue";
import Autocomplete from "../../../../components/new/form/autocomplete/AppAutoComplete.vue";

import { httpClient } from "../../../../services/HttpClient";
import GeminiService from "../../../../services/GeminiService/GeminiService";
import MessageNotificator from "../../../../services/MessageNotificator";
import { NotValidJsonStringError } from "../../../../services/Errors/NotValidJsonStringError";
import { TooManyRequestsError } from "../../../../services/Errors/TooManyRequestsError";
import { ServiceUnavailableError } from "../../../../services/Errors/ServiceUnavailableError";
import vClickOutside from 'click-outside-vue3';

export default {
    name: "TypesSelectionStep",
    components: { AppButton, AppCheckbox, Radio, Autocomplete },
    directives: {
        clickOutside: vClickOutside.directive
    },
    props: {
        selectionMode: {
            type: Object,
            default: null
        },
        selectedUuids: {
            type: Array,
            default: () => []
        },
        filters: {
            type: Object,
            default: () => ({})
        },
        selectedTypes: {
            type: Object,
            default: () => ({})
        }
    },
    emits: ['update:selectedTypes'],
    data() {
        return {
            search: '',
            activeTab: 'all',
            items: [],
            isLoading: false,
            isDropdownOpen: false,
            panelPosition: { top: 0, left: 0 },
            searchMatch: true,
            matchModes: ['exact'],
            createIfNotMatched: false,
            isMatching: false,
            tokenBalance: null,
            catalogCategories: []
        }
    },
    computed: {
        categoriesUrl() {
            return `/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/connection-process/categories`;
        },
        baseParams() {
            return this.selectionMode?.value === 'selected'
                ? { uuids: this.selectedUuids }
                : this.filters;
        },
        checkedItems() {
            return this.items.filter(item => item.selected);
        },
        chosenItems() {
            return this.items.filter(item => this.hasType(item));
        },
        notChosenItems() {
            return this.items.filter(item => !this.hasType(item));
        },
        filteredItems() {
            let result = this.items;

            if (this.activeTab === 'chosen') {
                result = result.filter(item => this.hasType(item));
            } else if (this.activeTab === 'not_chosen') {
                result = result.filter(item => !this.hasType(item));
            }

            if (this.search) {
                const search = this.search.toLowerCase();
                result = result.filter(item => item.name.toLowerCase().includes(search));
            }

            return result;
        },
        allSelected() {
            return this.items.length > 0 && this.items.every(item => item.selected);
        },
        hasNoAiBalance() {
            return this.tokenBalance !== null && this.tokenBalance <= 0;
        }
    },
    methods: {
        hasType(item) {
            return Boolean(this.selectedTypes[item.uuid]);
        },
        onActivate() {
            this.fetchCategories();
        },
        async fetchCategories() {
            this.isLoading = true;

            try {
                const response = await httpClient.get(this.categoriesUrl, this.baseParams);
                this.items = response.data.data.map(category => ({
                    uuid: category.uuid,
                    name: category.name,
                    productsCount: category.products_count,
                    selected: false
                }));
            } finally {
                this.isLoading = false;
            }
        },
        selectType(categoryUuid, value) {
            this.$emit('update:selectedTypes', { ...this.selectedTypes, [categoryUuid]: value });

            const item = this.items.find(item => item.uuid === categoryUuid);

            if (item) {
                item.selected = value !== null && value !== undefined;
            }
        },
        toggleAll(value) {
            this.items.forEach(item => { item.selected = value; });
        },
        toggleMatchMode(mode) {
            this.matchModes = this.matchModes.includes(mode)
                ? this.matchModes.filter(selectedMode => selectedMode !== mode)
                : [...this.matchModes, mode];
        },
        toggleDropdown() {
            this.isDropdownOpen = !this.isDropdownOpen;
        },
        closeDropdown() {
            this.isDropdownOpen = false;
        },
        isOutsidePanel(event) {
            return !(this.$refs.panelEl && this.$refs.panelEl.contains(event.target));
        },
        updatePanelPosition() {
            const toggleBtn = this.$refs.toggleBtn;

            if (!toggleBtn) {
                return;
            }

            const rect = toggleBtn.getBoundingClientRect();
            this.panelPosition = { top: rect.bottom + 6, left: rect.left };
        },
        applySelection() {
            this.closeDropdown();
            this.runAutoMatch();
        },
        async fetchTokenBalance() {
            const response = await httpClient.get(`/projects/${this.$route.params.projectUuid}/core/ai-transactions/balance`);
            this.tokenBalance = response.data.balance;
        },
        async runAutoMatch() {
            if (this.isMatching) {
                return;
            }

            const willSearch = this.searchMatch && this.matchModes.length > 0;
            if (!willSearch && !this.createIfNotMatched) {
                return;
            }

            this.isMatching = true;

            try {
                let unresolved = this.items.filter(item => item.selected);

                if (willSearch) {
                    if (this.matchModes.includes('exact')) {
                        unresolved = await this.runExactMatch(unresolved);
                    }

                    if (this.matchModes.includes('ai') && unresolved.length > 0) {
                        unresolved = await this.runAiMatch(unresolved);
                    }
                }

                if (this.createIfNotMatched && unresolved.length > 0) {
                    await this.createMissingTypes(unresolved);
                }
            } finally {
                this.isMatching = false;
            }
        },
        async runExactMatch(items) {
            const candidates = await this.fetchAllCatalogCategories();
            const unresolved = [];
            const matchedTypes = { ...this.selectedTypes };

            for (const item of items) {
                const found = this.findExactMatch(item, candidates);

                if (found) {
                    matchedTypes[item.uuid] = found;
                    item.selected = true;
                } else {
                    unresolved.push(item);
                }
            }

            this.$emit('update:selectedTypes', matchedTypes);

            return unresolved;
        },
        findExactMatch(item, candidates) {
            if (!item.name) {
                return null;
            }

            const normalized = item.name.trim().toLowerCase();
            return candidates.find(candidate => (candidate.name ?? '').trim().toLowerCase() === normalized) ?? null;
        },
        async runAiMatch(items) {
            const candidates = await this.fetchAllCatalogCategories();
            if (candidates.length === 0) {
                return items;
            }

            const geminiService = new GeminiService();
            const unresolved = [];

            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                let matched = false;

                try {
                    matched = await this.matchWithAi(item, candidates, geminiService);
                } catch (error) {
                    this.notifyAiError(error);
                }

                if (!matched) {
                    unresolved.push(item);
                }

                await this.fetchTokenBalance();
                if (this.hasNoAiBalance) {
                    new MessageNotificator().warning(this.$t('alerts.insufficient_ai_balance'));
                    unresolved.push(...items.slice(i + 1));
                    break;
                }
            }

            return unresolved;
        },
        async matchWithAi(item, candidates, geminiService) {
            const prompt = this.buildAiPrompt(item, candidates);

            let response = null;
            try {
                response = await geminiService.generateWithRetry(prompt);
            } finally {
                await geminiService.flushTokens(this.$route.params.projectUuid, 'ШІ підбір типів товарів');
            }

            const found = response?.uuid ? candidates.find(candidate => candidate.uuid === response.uuid) : null;
            if (found) {
                this.selectType(item.uuid, found);
                return true;
            }

            return false;
        },
        async createMissingTypes(items) {
            for (const item of items) {
                try {
                    const response = await httpClient.post(`/projects/${this.$route.params.projectUuid}/pim/types`, { name: item.name });
                    this.selectType(item.uuid, response.data.data);
                } catch (error) {
                    this.notifyAiError(error);
                }
            }
        },
        async fetchAllCatalogCategories() {
            if (this.catalogCategories.length > 0) {
                return this.catalogCategories;
            }

            const response = await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/types`, {
                limit: 100,
            });

            this.catalogCategories = response.data.data ?? [];
            return this.catalogCategories;
        },
        buildAiPrompt(item, candidates) {
            const candidatesJson = JSON.stringify(candidates.map(candidate => ({ uuid: candidate.uuid, name: candidate.name })), null, 2);

            return `
                You are matching a product category name from an external marketplace integration to the closest matching category from our internal product catalog.

                External category name: "${item.name}"

                Candidate internal catalog categories:
                ${candidatesJson}

                Task: choose the single best matching internal catalog category for the external category above, based on semantic similarity of the names.

                Rules:
                - Do not force a match. If none of the candidates is a reasonable match, return null.
                - Return ONLY a valid JSON object, with no explanations outside of it, in exactly this shape:
                { "uuid": "best-matching-category-uuid" }
                or, if there is no good match:
                { "uuid": null }
            `.trim();
        },
        notifyAiError(error) {
            if (error instanceof NotValidJsonStringError) {
                new MessageNotificator().error(this.$t('alerts.received_not_valid_json'));
            } else if (error instanceof TooManyRequestsError) {
                new MessageNotificator().error(this.$t('alerts.too_many_requests'));
            } else if (error instanceof ServiceUnavailableError) {
                new MessageNotificator().error(this.$t('alerts.service_unavailable'));
            } else {
                new MessageNotificator().error(this.$t('alerts.contact_support'));
            }
        }
    },
    watch: {
        isDropdownOpen(isOpen) {
            if (isOpen) {
                this.updatePanelPosition();
                window.addEventListener('scroll', this.updatePanelPosition, true);
                window.addEventListener('resize', this.updatePanelPosition);
            } else {
                window.removeEventListener('scroll', this.updatePanelPosition, true);
                window.removeEventListener('resize', this.updatePanelPosition);
            }
        },
        hasNoAiBalance(noBalance) {
            if (noBalance && this.matchModes.includes('ai')) {
                this.matchModes = this.matchModes.filter(mode => mode !== 'ai');
            }
        }
    },
    mounted() {
        this.fetchTokenBalance();
    },
    beforeUnmount() {
        window.removeEventListener('scroll', this.updatePanelPosition, true);
        window.removeEventListener('resize', this.updatePanelPosition);
    }
}
</script>
<style scoped>
.table-scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}
.table-bordered {
    border-top: 0px;
}
.matches-toolbar {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1.5rem;
    border-bottom: 1px solid #e5e7eb;
}
.toolbar-left {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    padding: 0.75rem 0 0.5rem;
}
.select-dropdown {
    position: relative;
}
.select-panel {
    position: fixed;
    z-index: 1020;
    width: 260px;
    padding: 1rem;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 4px 8px -2px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31);
}
.select-panel-group {
    margin-bottom: 1rem;
}
.select-panel-label {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 13px;
    color: #3c4553;
}
.select-panel-options {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}
.select-panel-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 13px;
    color: #3c4553;
    cursor: pointer;
}
.select-panel-footer {
    display: flex;
    justify-content: flex-end;
    padding-top: 0.75rem;
    border-top: 1px solid #e5e7eb;
}
.search-box {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 220px;
    height: 30px;
    padding: 0 0.25rem;
}
.search-icon {
    flex-shrink: 0;
    font-size: 14px;
    color: #9aa2ad;
}
.search-input {
    width: 100%;
    height: 100%;
    background: transparent;
    border: none;
    font-size: 13px;
    color: #3c4553;
}
.search-input:focus {
    outline: none;
}
.search-input::placeholder {
    color: #9aa2ad;
}
.tabs {
    display: flex;
    align-items: flex-end;
    gap: 1.5rem;
}
.tab {
    margin-bottom: -1px;
    padding: 1rem 0 0.75rem;
    border: none;
    border-bottom: 2px solid transparent;
    background: transparent;
    outline: none;
    font-size: 13px;
    color: #7b8492;
    transition: color 0.15s ease-out, border-color 0.15s ease-out;
}
.tab:hover {
    color: #3c4553;
}
.tab.active {
    border-bottom-color: #2f80ed;
    color: #2f80ed;
    font-weight: 500;
}
.tab-count {
    margin-left: 0.375rem;
    font-size: 12px;
    color: #9aa2ad;
}
.tab.active .tab-count {
    color: #2f80ed;
}
</style>
